/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import DS from 'ember-data';
import { task } from 'ember-concurrency';

import FormValidations from 'cio/utils/form-validations';
import AnalyticsService from 'cio/services/analytics';
import ApiCredentialModel from 'cio/models/api-credential';
import EnvironmentModel from 'cio/models/environment';
import { equal } from '@ember/object/computed';
import { tracked } from '@glimmer/tracking';
import isDestroyed from 'cio/utils/is-destroyed';

interface ApiCredentialsTableRowComponentArgs {
  key: ApiCredentialModel;
  readOnly: boolean;
  credentialCountForEnvironment: number;
  environmentId?: string;
  onUpdate: (credential: ApiCredentialModel) => unknown;
  onDelete: (credential: ApiCredentialModel) => unknown;
}

const Validations = {
  name(value: string) {
    if (isEmpty(value?.trim())) {
      return 'Please enter a name';
    }

    return true;
  },
  environment(value: EnvironmentModel | null) {
    if (!value) {
      return 'Please select a workspace';
    }

    return true;
  },
};

export default class ApiCredentialsTableRowComponent extends Component<ApiCredentialsTableRowComponentArgs> {
  @service analytics!: AnalyticsService;
  @service store!: DS.Store;

  @tracked isEditing = false;

  itemActionOptions = [
    {
      value: 'rename',
      label: 'Rename key',
    },
    {
      value: 'delete',
      label: 'Delete key',
    },
  ];
  validations: FormValidations;

  constructor(owner: unknown, args: ApiCredentialsTableRowComponentArgs) {
    super(owner, args);

    this.validations = FormValidations.create({ validations: Validations, context: this, modelKey: 'args.key' });

    if (!isEmpty(this.args.environmentId) && this.args.key.get('isNew')) {
      this.setEnvironment(this.args.environmentId as string);
    }
  }

  willDestroy() {
    super.willDestroy();

    if (this.isEditing && !isDestroyed(this.args.key)) {
      this.args.key.rollbackAttributes();
    }
  }

  get environments() {
    return this.store.peekAll('environment');
  }

  get trackingKey() {
    return isEmpty(this.args.environmentId) ? 'integration' : 'workspace';
  }

  @equal('args.credentialCountForEnvironment', 1) deleteDisabled!: boolean;

  @computed('environments.[]')
  get environmentSelectOptions() {
    let options = [{ label: 'Select workspace…', value: '' }];

    if (!isEmpty(this.args.environmentId)) {
      let environment = this.environments.findBy('id', this.args.environmentId);
      if (environment != null) {
        options.push({ label: environment.name, value: environment.id });
        return options;
      }
    }

    options = options.concat(this.environments.map((e) => ({ label: e.name, value: e.id })));
    return options;
  }

  @computed('validations.{isTouched,hasErrors}', 'args.{key.hasDirtyAttributes,submitTask.isRunning,readOnly}')
  get submitDisabled() {
    return (
      !this.validations.isTouched ||
      this.validations.hasErrors ||
      !this.args.key.hasDirtyAttributes ||
      (this.submitTask as any).isRunning ||
      this.args.readOnly
    );
  }

  submitTask = task(async () => {
    await this.validations.validateModel();
    await this.args.onUpdate(this.args.key);

    this.isEditing = false;
  });

  @action
  onCopy(copied: string) {
    this.analytics.track(this.trackingKey, 'api_credentials_copied', {
      credentialType: 'Tracking',
      credentialCopied: copied,
    });
  }

  @action
  rowAction({ value }: { value: string }) {
    switch (value) {
      case 'delete':
        this.args.onDelete(this.args.key);
        break;
      case 'rename':
        this.isEditing = true;
        break;
    }
  }

  @action
  setEnvironment(value: string) {
    this.args.key.set('environment', value ? this.environments.findBy('id', value) : null);
    this.validations.validateAttribute('environment');
  }

  @action
  cancelEditing() {
    this.isEditing = false;
    this.args.key.rollbackAttributes();
  }

  @action
  trackDeleteCredentialStarted() {
    this.analytics.track(this.trackingKey, 'api_credentials_deletion_started', { keyType: 'Tracking' });
  }

  @action
  onNameChange(name: string) {
    this.args.key.set('name', name);
    this.validations.validateAttribute('name');
  }
}
